"use client";

import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useCurrency } from "@/contexts/CurrencyContext";
import { cn } from "@/lib/utils";
import { buildTimeslotV2PriceGroups } from "@/lib/utils/timeslot-matrix";
import {
  buildDailyV2PriceGroups,
  type MergedDailySubProduct,
} from "@/lib/utils/daily-matrix";
import type { MarkedUpPricingSource } from "@/lib/pricing/base-plus-extra";
import {
  headlineBaseDays,
  pickDailyV2Headline,
  pickTimeslotV2Headline,
  pickV1HeadlineTier,
  selectV1PricingTiers,
  v1TierPrice,
} from "@/lib/pricing/headline-price";
import {
  buildSeasonTable,
  formatSeasonRange,
  groupSubsByPrice,
  type SeasonTable,
  type SeasonTableSub,
} from "@/lib/pricing/season-table";
import type { PricingSeason, ProductPricing } from "@/types";
import type { SupportedCurrency } from "@/lib/utils/currency";

interface PricingInfoProps {
  pricing: ProductPricing[];
  rentalUnit: "daily" | "timeslot";
  currency: SupportedCurrency;
  locale: string;
  onViewDetails?: () => void;
  /**
   * Timeslot v2 매트릭스 (Phase B-2) — 언어 병합 완료본(mergeTimeslotMatrixConfig).
   * 존재하면 v1 pricing 대신 매트릭스 셀 기반으로 표시 (v2 상품은 pricing이 빈 배열).
   */
  matrix?: {
    timeOptions: Array<{ id: string; label: string }>;
    subProducts: Array<{ id: string; name: string; prices: number[] }>;
  } | null;
  /**
   * 일일대여 v2 세부 상품 (08-17) — 존재하면 daily 모드가 v1 `pricing` 대신
   * 세부 상품별 기간 티어로 표시한다(v2 상품은 product_pricing daily 행이 없다).
   */
  dailyMatrix?: { subProducts: MergedDailySubProduct[] } | null;
  /** v2 행 라벨 중복 생략 판별용 상품명 (세부상품명 = 상품명이면 옵션 라벨만 — 07-17 규칙) */
  productName?: string;
  /**
   * 요금 요약 원천 (`specifications.pricing`, 서버에서 day_prices 와 **같은
   * 마크업**을 적용한 표시가 — `lib/api/products/detail.ts`). 존재하고
   * `rentalUnit === "daily"` + `dailyMatrix` 가 있으면 **기존 "요금 상세"
   * 펼침의 행 내용만** 교체한다(같은 회색 리스트·같은 행 문법) — 긴 "N일 이상
   * 단가" 사다리 행 대신 기본 요금/추가 1일/패키지 행.
   * null/미전달이면 기존 사다리 그대로(무회귀).
   * 예약 총액은 서버가 미리 계산한 고객가 표(`product.policyTable`)로 따로 낸다.
   */
  pricingSource?: MarkedUpPricingSource | null;
  /**
   * 성수기 요금(마크업 반영본, `product.pricing_seasons`) — 일일대여만. 있으면 헤드라인에
   * "부터"를 붙이고 요금 상세에 기간별 요금표를 둔다(10-16 C안). 시즌 이름은 파트너
   * 원문이라 쓰지 않고 날짜만 보인다.
   */
  seasons?: PricingSeason[] | null;
}


export default function PricingInfo({
  pricing,
  rentalUnit,
  currency,
  locale,
  onViewDetails,
  matrix,
  dailyMatrix,
  productName,
  pricingSource,
  seasons,
}: PricingInfoProps) {
  const t = useTranslations("product.pricing");
  const { formatWithCodeFromSource } = useCurrency();
  // 기본 접힘 — v1·v2 공통 (07-17 사용자 확정)
  const [isExpanded, setIsExpanded] = useState(false);

  // Timeslot v2: 세부 상품별 그룹 리스트 (07-17 사용자 확정 A안 —
  // 목업: locarora-admin/docs/preview-pricing-info.html).
  // >0 셀만, 헤더 생략(중복 생략·단일 상품) 규칙은 buildTimeslotV2PriceGroups.
  //
  // ⚠️ rental_unit 축 가드 (08-17) — BookingCard의 `isTimeslotV2`
  // (`pricingMode === "timeslot" && !!timeslotMatrix`) 규약을 그대로 미러한다.
  // 타임슬롯 → 일일대여로 전환된 상품은 `sub_product_prices` 잔재 행이 남아
  // 매트릭스가 non-null로 만들어진다. 축 가드 없이 matrix만 보면 daily 상품의
  // 요금 상세에 타임슬롯 매트릭스가 그려져, 같은 화면의 BookingCard(일일 티어)와
  // 가격 표시가 갈라진다.
  const v2Groups = useMemo(
    () =>
      rentalUnit === "timeslot" && matrix
        ? buildTimeslotV2PriceGroups(matrix, productName)
        : [],
    [rentalUnit, matrix, productName],
  );
  const v2Rows = useMemo(() => v2Groups.flatMap((g) => g.rows), [v2Groups]);

  // v2 헤드라인 = 최저가 셀 + "/ 세부상품명 · 옵션라벨" 꼬리 (07-17 사용자 확정 복원)
  // — 라벨은 그룹 행과 동일 소스: 그룹 헤더가 생략된 케이스(중복 생략·단일 그룹)면
  //   옵션 라벨만 (buildTimeslotV2PriceGroups의 name null 규칙 재사용)
  const v2Display = useMemo(() => {
    const best = pickTimeslotV2Headline(v2Groups);
    if (!best) return null;
    return {
      price: best.price,
      label: best.groupName ? `${best.groupName} · ${best.rowLabel}` : best.rowLabel,
    };
  }, [v2Groups]);

  // Daily v2 (08-17): 세부 상품별 기간 티어 그룹 — 타임슬롯 v2 그룹 규칙 미러
  // (>0 행만, 헤더 생략 규칙 동일). 행 라벨은 v1 daily와 같은 `{days}일 이상`.
  const dailyGroups = useMemo(
    () =>
      rentalUnit === "daily" && dailyMatrix
        ? buildDailyV2PriceGroups(dailyMatrix.subProducts, productName)
        : [],
    [rentalUnit, dailyMatrix, productName],
  );
  const dailyV2Rows = useMemo(
    () => dailyGroups.flatMap((g) => g.rows),
    [dailyGroups],
  );
  // 헤드라인 = 최저 단가 셀 + "/ N일 이상" (그룹 헤더가 있으면 세부상품명 접두)
  const dailyV2Display = useMemo(() => {
    const best = pickDailyV2Headline(dailyGroups, !!pricingSource);
    if (!best) return null;
    // 기본 요금이 2일 요금이면 "기본 요금 (2일)" — 큰 숫자가 몇 일 값인지 함께(10-15).
    const baseDays = headlineBaseDays(best, dailyMatrix?.subProducts ?? [], pricingSource);
    const tierLabel = best.isBase
      ? baseDays
        ? t("basePlusExtra.baseWithDays", { days: baseDays })
        : t("basePlusExtra.base")
      : t("daysOrMore", { days: best.minDays });
    return {
      price: best.price,
      label: best.groupName ? `${best.groupName} · ${tierLabel}` : tierLabel,
    };
  }, [dailyGroups, t, pricingSource, dailyMatrix]);

  // 요금 요약 (base + 추가 1일 + 패키지) — daily v2 + 원천이 있을 때만.
  // 세부 상품 축은 `sub_display_order`(= pricingSource 의 키)로 맞춘다.
  const summarySections = useMemo(() => {
    if (rentalUnit !== "daily" || !dailyMatrix || !pricingSource) return [];
    const sections: Array<{
      id: string;
      name: string;
      displayOrder: number;
      source: (typeof pricingSource.subProducts)[string];
    }> = [];
    for (const sub of dailyMatrix.subProducts) {
      const source = pricingSource.subProducts[String(sub.displayOrder)];
      if (!source) continue; // 원천에 없는 세부 상품 = 요약 생략(무회귀)
      sections.push({
        id: sub.id,
        name: sub.name,
        displayOrder: sub.displayOrder,
        source,
      });
    }
    return sections;
  }, [rentalUnit, dailyMatrix, pricingSource]);

  const pricingTiers = useMemo(
    () => selectV1PricingTiers(pricing, rentalUnit, locale),
    [pricing, rentalUnit, locale],
  );

  const displayInfo = useMemo(() => {
    const tier = pickV1HeadlineTier(pricingTiers, rentalUnit);
    if (!tier) return { price: 0, label: "" };
    if (rentalUnit === "daily") {
      return {
        price: v1TierPrice(tier, rentalUnit),
        label: t("daysOrMore", { days: tier.min_days || 1 }),
      };
    }
    return {
      price: v1TierPrice(tier, rentalUnit),
      label: tier.label || `${tier.duration_hours}${t("hours")}`,
    };
  }, [pricingTiers, rentalUnit, t]);

  // Base price for discount calculation (first tier, usually min_days=1)
  const basePrice = useMemo(() => {
    if (rentalUnit !== "daily" || pricingTiers.length === 0) return 0;
    const firstTier = pricingTiers[0];
    return firstTier?.price_per_day || 0;
  }, [pricingTiers, rentalUnit]);

  // v2면 매트릭스 셀 기반으로 스왑 — v1 경로 값 계산은 그대로(무회귀), 표시만 교체
  const isV2 = v2Display != null;
  // Daily v2 (08-17) — 타임슬롯 v2와 배타(rentalUnit이 축을 가른다)
  const isDailyV2 = !isV2 && dailyV2Display != null;
  const headline = isV2
    ? v2Display
    : isDailyV2
    ? dailyV2Display
    : displayInfo;
  // 기간별 요금표 (10-16 C안) — 세부 상품마다 대표 요금 1칸을 시즌별로 비교한다.
  const seasonTable = useMemo<{ table: SeasonTable; subs: SeasonTableSub[] } | null>(() => {
    if (rentalUnit !== "daily" || !seasons?.length) return null;
    let subs: SeasonTableSub[] = [];
    if (isDailyV2 && summarySections.length > 0) {
      subs = summarySections.map((section) => {
        const unitDays = section.source.baseDays === 1 ? 1 : 2;
        return {
          order: section.displayOrder,
          name: section.name,
          unitDays,
          tierMinDays: unitDays,
          basePrice: section.source.base,
        };
      });
    } else if (isDailyV2) {
      subs = (dailyMatrix?.subProducts ?? []).flatMap((sub) => {
        const tier = sub.tiers.find((x) => x.pricePerDay > 0);
        return tier
          ? [{ order: sub.displayOrder, name: sub.name, unitDays: 1, tierMinDays: tier.minDays, basePrice: tier.pricePerDay }]
          : [];
      });
    } else if (!isV2 && pricingTiers[0]?.price_per_day) {
      const tier = pricingTiers[0];
      subs = [{ order: null, name: "", unitDays: 1, tierMinDays: tier.min_days || 1, basePrice: tier.price_per_day || 0 }];
    }
    const table = buildSeasonTable(subs, seasons);
    return table ? { table, subs } : null;
  }, [rentalUnit, seasons, isDailyV2, isV2, summarySections, dailyMatrix, pricingTiers]);

  // 기본 요금 = 추가 1일 · 1일 기준 · 패키지 없음이면 "기본 요금/추가 1일" 행이 표와
  // 같은 말을 반복한다 — 표 하나로 대신한다(C안: 펼친 행 8줄 → 3줄).
  const seasonReplacesSummary =
    !!seasonTable &&
    isDailyV2 &&
    summarySections.length > 0 &&
    summarySections.every(
      (s) =>
        s.source.baseDays === 1 &&
        s.source.extraDay === s.source.base &&
        s.source.packages.length === 0,
    );

  // 헤드라인은 시즌까지 포함한 최저가 + "부터" — 대표 요금(기본 요금)일 때만 표의
  // 최저가와 같은 단위라 비교한다(사다리·v1 헤드라인은 장기 단가일 수 있다).
  const headlinePrice =
    seasonTable && isDailyV2 && summarySections.length > 0
      ? Math.min(headline.price, seasonTable.table.minPrice)
      : headline.price;

  // 요금 정책 행이 사다리 행을 대체하므로, 티어가 1개뿐이어도 정책 행이 있으면
  // 펼침을 연다 (없으면 base/추가 1일/패키지가 화면에서 사라진다).
  const hasMultipleTiers =
    !!seasonTable ||
    (isV2
      ? v2Rows.length > 1
      : isDailyV2
      ? summarySections.length > 0 || dailyV2Rows.length > 1
      : pricingTiers.length > 1);

  // v2 상품은 pricing이 빈 배열 — 매트릭스 셀/기간 티어가 있으면 가격 블록 유지
  if ((!pricing || pricing.length === 0) && !isV2 && !isDailyV2) {
    return null;
  }

  return (
    <div className="pb-2">
      {/* Main price display */}
      <div className="flex min-w-0 flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <div className="flex min-w-0 max-w-full flex-wrap items-baseline gap-x-2 gap-y-0.5">
          {seasonTable && t("fromPrefix") && (
            <span className="shrink-0 text-sm text-gray-600">{t("fromPrefix")}</span>
          )}
          <span className="shrink-0 text-3xl font-bold tabular-nums text-gray-900">
            {formatWithCodeFromSource(headlinePrice, currency)}
          </span>
          <span className="min-w-0 text-sm leading-5 text-gray-600 sm:truncate">
            {seasonTable && t("fromSuffix") ? `${t("fromSuffix")} ` : ""}/ {headline.label}
          </span>
        </div>

        {hasMultipleTiers && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            className="inline-flex shrink-0 items-center gap-1 self-end whitespace-nowrap text-sm font-medium text-gray-600 hover:text-gray-900 sm:self-auto"
          >
            {t("rateDetails")}
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform duration-200",
                isExpanded && "rotate-180"
              )}
            />
          </button>
        )}
      </div>

      {/* 사이트 이용 수수료 사전 고지는 제거됨 (2026-07-17 사용자 결정) —
          결제 전 고지는 체크아웃 요약의 상시 분해(상품 금액/사이트 이용 수수료/
          총 결제금액)가 담당한다. */}

      {/* Tiered pricing details (collapsible) */}
      {hasMultipleTiers && (
        <div
          className={cn(
            "transition-all duration-200",
            isExpanded
              ? "mt-3 max-h-96 opacity-100 overflow-y-auto"
              : "max-h-0 opacity-0 overflow-hidden"
          )}
        >
          <div className="bg-gray-50 rounded-lg p-3 space-y-0">
            {/* Timeslot v2: 세부 상품별 그룹 (07-17 A안) — 회색 박스 문법은 그대로,
                안의 행 구조만 그룹 헤더(상품명) + 들여쓴 옵션 행으로 변경 */}
            {isV2
              ? v2Groups.length === 1 && !v2Groups[0].name
                ? /* 단일 상품(헤더 생략) — 옵션 행만 = 현행 v1 행 모양 (마이그레이션 상품) */
                  v2Groups[0].rows.map((row, index) => (
                    <div
                      key={row.key}
                      className={cn(
                        "flex items-center justify-between gap-3 py-2",
                        index < v2Groups[0].rows.length - 1 &&
                          "border-b border-gray-100",
                      )}
                    >
                      <span className="text-sm text-gray-600 min-w-0 break-keep">
                        {row.label}
                      </span>
                      <span className="text-sm font-medium text-gray-900 whitespace-nowrap shrink-0 tabular-nums">
                        {formatWithCodeFromSource(row.price, currency)}
                      </span>
                    </div>
                  ))
                : /* 그룹: 상품명 헤더 1번 + 아래 옵션 행 — 그룹 사이 구분선은
                     행 구분선(gray-100)보다 한 단계 진한 gray-200 (목업 A안) */
                  v2Groups.map((group, groupIndex) => (
                    <div
                      key={group.id}
                      className={cn(
                        "pt-1.5 pb-0.5",
                        groupIndex > 0 && "mt-1 border-t border-gray-200 pt-2.5",
                      )}
                    >
                      {group.name && (
                        <div className="text-sm font-semibold text-gray-900 break-keep">
                          {group.name}
                        </div>
                      )}
                      {group.rows.map((row) => (
                        <div
                          key={row.key}
                          className={cn(
                            "flex items-center justify-between gap-3 py-1.5",
                            // 헤더가 있는 그룹만 옵션 행 들여쓰기
                            group.name && "pl-3",
                          )}
                        >
                          <span className="text-sm text-gray-600 min-w-0 break-keep">
                            {row.label}
                          </span>
                          <span className="text-sm font-medium text-gray-900 whitespace-nowrap shrink-0 tabular-nums">
                            {formatWithCodeFromSource(row.price, currency)}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))
              : seasonReplacesSummary
              ? null
              : isDailyV2
              ? summarySections.length > 0
                ? /* 요금 정책 행 (09-18 사용자 확정) — `pricingSource` 가 있으면
                     **같은 회색 리스트 · 같은 행 문법**에 티어 사다리 행 대신
                     기본 요금 / 추가 1일 / 패키지 / "{N}일 총액" 행을 그린다.
                     별도 블록을 만들지 않는다(내용만 교체). */
                  summarySections.map((section, sectionIndex) => {
                    // 헤더 생략 규칙은 사다리 그룹(buildDailyV2PriceGroups) 미러 —
                    // 세부 상품이 여럿일 때만 이름 행을 둔다.
                    const showName =
                      summarySections.length > 1 && !!section.name;
                    const flat = summarySections.length === 1;
                    const rows: Array<{
                      key: string;
                      label: string;
                      value: string;
                      /** 패키지 행만 — "기본+추가일" 대비 절약률(%), 0이면 표시 안 함 */
                      discountPercent?: number;
                    }> = [
                      {
                        key: "base",
                        label: `${t("basePlusExtra.base")} (${t("basePlusExtra.days", { days: section.source.baseDays === 1 ? 1 : 2 })})`,
                        value: formatWithCodeFromSource(section.source.base, currency),
                      },
                    ];
                    if (section.source.extraDay > 0) {
                      rows.push({
                        key: "extraDay",
                        label: t("basePlusExtra.extraDay"),
                        value: t("basePlusExtra.extraDayValue", {
                          price: formatWithCodeFromSource(
                            section.source.extraDay,
                            currency,
                          ),
                        }),
                      });
                    }
                    for (const pkg of section.source.packages) {
                      // 패키지 절약률 = 같은 일수를 "기본 + 추가일"로 냈을 때 대비.
                      // 파트너가 실제로 깎아 주는 금액이라 사다리의 단가 착시(2일 -50%)와
                      // 달리 의미가 있다. 표시용 — 결제는 정책에서 총액을 계산한다.
                      const payAsYouGo =
                        section.source.base +
                        section.source.extraDay * Math.max(0, pkg.days - (section.source.baseDays === 1 ? 1 : 2));
                      const discountPercent =
                        payAsYouGo > 0 && pkg.price < payAsYouGo
                          ? Math.round(
                              ((payAsYouGo - pkg.price) / payAsYouGo) * 100,
                            )
                          : 0;
                      rows.push({
                        key: `pkg-${pkg.days}`,
                        label:
                          pkg.days === 7
                            ? t("basePlusExtra.week")
                            : pkg.days === 30
                              ? t("basePlusExtra.month")
                              : t("basePlusExtra.days", { days: pkg.days }),
                        value: formatWithCodeFromSource(pkg.price, currency),
                        discountPercent,
                      });
                    }
                    return (
                      <div
                        key={section.id}
                        className={cn(
                          !flat && "pt-1.5 pb-0.5",
                          !flat &&
                            sectionIndex > 0 &&
                            "mt-1 border-t border-gray-200 pt-2.5",
                        )}
                      >
                        {showName && (
                          <div className="text-sm font-semibold text-gray-900 break-keep">
                            {section.name}
                          </div>
                        )}
                        {rows.map((row, rowIndex) => (
                          <div
                            key={row.key}
                            className={cn(
                              "flex items-center justify-between gap-3",
                              flat ? "py-2" : cn("py-1.5", showName && "pl-3"),
                              flat &&
                                rowIndex < rows.length - 1 &&
                                "border-b border-gray-100",
                            )}
                          >
                            <span className="text-sm text-gray-600 min-w-0 break-keep">
                              {row.label}
                              {(row.discountPercent ?? 0) > 0 && (
                                <span className="text-xs font-medium text-red-500 ml-1.5">
                                  {t("discount", {
                                    percent: row.discountPercent,
                                  })}
                                </span>
                              )}
                            </span>
                            <span className="text-sm font-medium text-gray-900 whitespace-nowrap shrink-0 tabular-nums">
                              {row.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    );
                  })
                : /* Daily v2 (08-17): 세부 상품별 기간 티어 그룹 — 타임슬롯 v2 그룹
                   마크업 그대로, 행 라벨만 `{days}일 이상` + `/일` 접미어 */
                dailyGroups.map((group, groupIndex) => {
                  const groupBase = group.rows[0]?.price ?? 0;
                  const flat = dailyGroups.length === 1 && !group.name;
                  return (
                    <div
                      key={group.id}
                      className={cn(
                        !flat && "pt-1.5 pb-0.5",
                        !flat &&
                          groupIndex > 0 &&
                          "mt-1 border-t border-gray-200 pt-2.5",
                      )}
                    >
                      {group.name && (
                        <div className="text-sm font-semibold text-gray-900 break-keep">
                          {group.name}
                        </div>
                      )}
                      {group.rows.map((row, rowIndex) => {
                        const discountPercent =
                          groupBase > 0 && row.price < groupBase
                            ? Math.round(
                                ((groupBase - row.price) / groupBase) * 100,
                              )
                            : 0;
                        return (
                          <div
                            key={row.key}
                            className={cn(
                              "flex items-center justify-between gap-3",
                              flat
                                ? "py-2"
                                : cn("py-1.5", group.name && "pl-3"),
                              flat &&
                                rowIndex < group.rows.length - 1 &&
                                "border-b border-gray-100",
                            )}
                          >
                            <span className="text-sm text-gray-600 min-w-0 break-keep">
                              {t("daysOrMore", { days: row.minDays })}
                              {discountPercent > 0 && (
                                <span className="text-xs font-medium text-red-500 ml-1.5">
                                  {t("discount", { percent: discountPercent })}
                                </span>
                              )}
                            </span>
                            <span className="text-sm font-medium text-gray-900 whitespace-nowrap shrink-0 tabular-nums">
                              {formatWithCodeFromSource(row.price, currency)}
                              <span className="text-gray-500 font-normal">
                                {t("perDay")}
                              </span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              : pricingTiers.map((tier, index) => {
              const price =
                rentalUnit === "daily"
                  ? tier.price_per_day || 0
                  : tier.price || 0;
              const discountPercent =
                rentalUnit === "daily" && basePrice > 0 && price < basePrice
                  ? Math.round(((basePrice - price) / basePrice) * 100)
                  : 0;

              return (
                <div
                  key={tier.id}
                  className={cn(
                    // gap-3 + 라벨 min-w-0 + 가격 nowrap/shrink-0/tabular —
                    // 가격 중간 꺾임("¥22,4/04") 방지 공통 픽스 (07-17, v2와 동일)
                    "flex items-center justify-between gap-3 py-2",
                    index < pricingTiers.length - 1 && "border-b border-gray-100"
                  )}
                >
                  <span className="text-sm text-gray-600 min-w-0 break-keep">
                    {rentalUnit === "daily"
                      ? t("daysOrMore", { days: tier.min_days || 1 })
                      : tier.label || `${tier.duration_hours}${t("hours")}`}
                    {discountPercent > 0 && (
                      <span className="text-xs font-medium text-red-500 ml-1.5">
                        {t("discount", { percent: discountPercent })}
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-sm font-medium text-gray-900 whitespace-nowrap tabular-nums">
                      {formatWithCodeFromSource(price, currency)}
                      {rentalUnit === "daily" && (
                        <span className="text-gray-500 font-normal">
                          {t("perDay")}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
            {seasonTable && (
              <SeasonRates
                table={seasonTable.table}
                subs={seasonTable.subs}
                standalone={seasonReplacesSummary}
                currency={currency}
              />
            )}
          </div>
          {/* 리스트 아래 캡션 — 요금 정책 행일 때만 (기존 note 문구 그대로) */}
          {isDailyV2 && summarySections.length > 0 && !seasonReplacesSummary && (
            <p className="mt-2 text-xs text-gray-500 break-keep">
              {t("basePlusExtra.note")}
            </p>
          )}
          {seasonTable && (
            <p className="mt-1 text-xs text-gray-500 break-keep">{t("season.note")}</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * 기간별 요금표 — 세부 상품 금액이 모두 같으면 "기간 | 금액" 한 줄씩, 다르면 기간 머리 줄
 * 아래 세부 상품 줄(같은 금액끼리 한 줄, 3~4개여도 아래로 — 10-16 사용자 결정).
 * `standalone` = 요금 상세가 이 표뿐(기본 요금 행 대체). 아니면 위 행과 구분선으로 가른다.
 */
function SeasonRates({
  table,
  subs,
  standalone,
  currency,
}: {
  table: SeasonTable;
  subs: SeasonTableSub[];
  standalone: boolean;
  currency: SupportedCurrency;
}) {
  const t = useTranslations("product.pricing");
  const { formatWithCodeFromSource } = useCurrency();
  const locale = useLocale();
  const label = (period: SeasonTable["periods"][number]) =>
    period.ranges.length === 0
      ? t("season.other")
      : period.ranges.map((range) => formatSeasonRange(range, locale)).join(" · ");
  const unitDays = subs.every((s) => s.unitDays === subs[0].unitDays)
    ? subs[0].unitDays
    : null;

  return (
    <div className={cn(!standalone && "mt-1 border-t border-gray-200 pt-2.5")}>
      {!standalone && (
        <div className="text-sm font-semibold text-gray-900 break-keep">
          {t("season.title")}
        </div>
      )}
      {table.uniform ? (
        <>
          <div className={cn("flex justify-between gap-3 pb-1 text-xs text-gray-500", !standalone && "pl-3 pt-1")}>
            <span>{t("season.period")}</span>
            {unitDays && <span>{t("basePlusExtra.days", { days: unitDays })}</span>}
          </div>
          {table.periods.map((period, index) => (
            <div
              key={index}
              className={cn(
                "flex items-center justify-between gap-3 py-2",
                !standalone && "pl-3",
                index < table.periods.length - 1 && "border-b border-gray-100",
              )}
            >
              <span className="text-sm text-gray-600 min-w-0 break-keep tabular-nums">
                {label(period)}
              </span>
              <span className="text-sm font-medium text-gray-900 whitespace-nowrap shrink-0 tabular-nums">
                {formatWithCodeFromSource(period.prices[0], currency)}
              </span>
            </div>
          ))}
        </>
      ) : (
        table.periods.map((period, index) => (
          <div
            key={index}
            className={cn(
              "pt-1.5 pb-0.5",
              index > 0 && "mt-1 border-t border-gray-100 pt-2",
            )}
          >
            <div className={cn("text-[13px] font-semibold text-gray-900 break-keep tabular-nums", !standalone && "pl-3")}>
              {label(period)}
            </div>
            {groupSubsByPrice(subs, period.prices).map((row) => (
              <div
                key={row.names.join("|")}
                className={cn("flex items-center justify-between gap-3 py-1.5", standalone ? "pl-3" : "pl-6")}
              >
                <span className="text-sm text-gray-600 min-w-0 break-keep">
                  {row.names.join(" · ")}
                </span>
                <span className="text-sm font-medium text-gray-900 whitespace-nowrap shrink-0 tabular-nums">
                  {formatWithCodeFromSource(row.price, currency)}
                </span>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}
